import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Modal } from "@/components/Modal";
import { Heart, ArrowLeft, Backpack, Hand } from "lucide-react";
import { BAG_PRODUCTS } from "@/data/bagProducts";

// A hand-drawn CSS backpack that "unzips" to spill its real contents.
const BagArt = ({ open }) => (
  <div className="relative mx-auto h-44 w-40 select-none">
    {/* straps */}
    <div className="absolute left-6 top-1 h-16 w-3 origin-top -rotate-6 rounded-full bg-[#8a3322]" />
    <div className="absolute right-6 top-1 h-16 w-3 origin-top rotate-6 rounded-full bg-[#8a3322]" />
    {/* body */}
    <div className="absolute inset-x-1 bottom-0 top-8 rounded-[26px] border-[3px] border-kirana-brown bg-[#C84B31] shadow-inner">
      {/* top flap */}
      <motion.div
        initial={false}
        animate={{ rotateX: open ? -150 : 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 12 }}
        style={{ transformOrigin: "top center", transformStyle: "preserve-3d" }}
        className="absolute inset-x-2 -top-1 h-14 rounded-[22px] border-[3px] border-kirana-brown bg-[#b5432b]"
      >
        <div className="absolute left-1/2 top-2 h-5 w-8 -translate-x-1/2 rounded-md border-2 border-kirana-brown bg-[#e8b04b]" />
      </motion.div>
      {/* front pocket + zipper */}
      <div className="absolute inset-x-3 bottom-3 top-16 rounded-[18px] border-[3px] border-kirana-brown bg-[#d85a3f]">
        <div className="absolute inset-x-0 top-2 flex items-center">
          <div className="mx-3 h-1 flex-1 rounded bg-kirana-brown/60" />
        </div>
        <div className="absolute left-1/2 top-1 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-kirana-brown bg-[#e8b04b]" />
      </div>
    </div>
  </div>
);

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
};
const pop = {
  hidden: { opacity: 0, y: 26, scale: 0.6 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 18 } },
};

export const BagOpenModal = ({ open, onClose, unlocked, onAdd, itemId }) => {
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (open) { setOpened(false); setSelected(null); setImgIdx(0); }
  }, [open]);

  const handleOpen = () => {
    setOpened(true);
    if (itemId && !unlocked) onAdd(itemId);
  };

  const pick = (p) => { setSelected(p); setImgIdx(0); };
  const gallery = selected?.images || (selected ? [selected.image] : []);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} testId="bag-open-modal" maxWidth="max-w-2xl">
      <div className="p-5 sm:p-7">
        <AnimatePresence mode="wait">
          {/* ---- CLOSED ---- */}
          {!opened && (
            <motion.div key="closed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center text-center">
              <h2 className="font-signboard text-3xl font-bold text-kirana-brown">My School Bag</h2>
              <p className="mt-1 max-w-sm text-sm italic text-kirana-brown/70">
                "Bag itna heavy tha ki school se ghar aate-aate exercise ho jaati thi."
              </p>
              <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} className="mt-4">
                <BagArt open={false} />
              </motion.div>
              <button data-testid="bag-open-btn" onClick={handleOpen}
                className="mt-5 flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-6 py-2.5 font-bold text-white transition-transform hover:scale-[1.04] hard-shadow">
                <Hand className="h-5 w-5" /> Unzip &amp; open the bag
              </button>
            </motion.div>
          )}

          {/* ---- OPEN: GRID ---- */}
          {opened && !selected && (
            <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-signboard flex items-center gap-2 text-2xl font-bold text-kirana-brown">
                    <Backpack className="h-6 w-6" /> What's Inside?
                  </h2>
                  <p className="text-xs text-kirana-brown/70">Tap any item to remember it</p>
                </div>
                {unlocked && (
                  <span data-testid="bag-memory-unlocked" className="flex items-center gap-1.5 rounded-md border-2 border-kirana-red bg-kirana-red/10 px-3 py-1.5 text-sm font-bold text-kirana-red">
                    <Heart className="h-4 w-4 fill-kirana-red" /> Memory unlocked
                  </span>
                )}
              </div>

              <motion.div variants={stagger} initial="hidden" animate="show"
                className="relative mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {/* soft open, empty bag back-layer so items feel like they came out of it */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-44 w-40 -translate-x-1/2 -translate-y-1/2">
                  <div className="absolute inset-x-2 bottom-0 top-6 rounded-[26px] border-4 border-dashed border-[#C84B31]/25 bg-[#C84B31]/10" />
                  <div className="absolute inset-x-6 -top-1 h-12 rounded-[22px] border-4 border-dashed border-[#C84B31]/20" style={{ transform: "perspective(200px) rotateX(-45deg)", transformOrigin: "top" }} />
                </div>
                {BAG_PRODUCTS.map((p) => (
                  <motion.button key={p.id} variants={pop} data-testid={`bag-product-${p.id}`} onClick={() => pick(p)}
                    whileHover={{ scale: 1.06, rotate: -1 }} whileTap={{ scale: 0.96 }}
                    className="group relative z-10 flex flex-col overflow-hidden rounded-md border-[3px] border-kirana-brown text-left hard-shadow-sm"
                    style={{ background: p.color }}>
                    <div className="flex h-20 items-center justify-center bg-[#FDF6E3] p-1">
                      <img src={p.image} alt={p.name} className="h-full w-full object-contain" loading="lazy" />
                    </div>
                    <div className="bg-card px-1.5 py-1">
                      <span className="line-clamp-1 text-[11px] font-bold text-kirana-brown">{p.name}</span>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* ---- OPEN: DETAIL ---- */}
          {opened && selected && (
            <motion.div key="detail" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <button data-testid="bag-back-btn" onClick={() => setSelected(null)}
                className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5 text-sm font-bold text-kirana-brown hover:bg-kirana-gold">
                <ArrowLeft className="h-4 w-4" /> Back to bag
              </button>

              <div className="mt-3 flex h-52 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown bg-[#FDF6E3]">
                <img data-testid="bag-detail-image" src={gallery[imgIdx]} alt={selected.name} className="h-full w-full object-contain" />
              </div>

              {gallery.length > 1 && (
                <div className="mt-2 flex justify-center gap-2">
                  {gallery.map((g, i) => (
                    <button key={g} data-testid={`bag-thumb-${i}`} onClick={() => setImgIdx(i)}
                      className={`h-12 w-12 overflow-hidden rounded border-2 ${i === imgIdx ? "border-kirana-red" : "border-kirana-brown/40"}`}>
                      <img src={g} alt="" className="h-full w-full object-contain bg-[#FDF6E3]" />
                    </button>
                  ))}
                </div>
              )}

              <h3 data-testid="bag-detail-name" className="mt-3 font-signboard text-3xl font-bold text-kirana-brown">{selected.name}</h3>
              <span className="mt-1 inline-block rounded border-2 border-kirana-brown bg-kirana-gold px-2 py-0.5 text-xs font-bold text-kirana-brown">{selected.tag}</span>
              <p className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-[repeating-linear-gradient(#FDF6E3,#FDF6E3_22px,#cdd4e6_23px)] p-3 text-sm italic text-kirana-brown">
                "{selected.memory}"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
};
